export interface CsvPreview {
  /** Nome do arquivo, ou "conteúdo colado" / "CSV normalizado por IA". */
  origem: string;
  headers: string[];
  rows: Record<string, string>[];
  /** Total de linhas de dado no arquivo (rows pode vir truncado pra preview). */
  totalRows: number;
  /** TODAS as linhas de dado, indexadas pelo numero de linha do arquivo (2 =
   *  primeira linha de dado, igual a numeracao dos erros do backend) — usado
   *  pra mostrar contexto ("qual servidor era esse?") junto de cada erro. */
  linhas: Record<number, Record<string, string>>;
}

/**
 * Parser de preview no cliente — mesma lógica de detecção de separador e campo
 * entre aspas de `apps/api/src/_shared/csv.ts` (fonte de verdade da importação
 * de verdade). Isto aqui é só pra mostrar uma prévia fiel ANTES de confirmar o
 * envio — se as regras de parse mudarem no backend, replicar aqui também.
 */
export function parseCsvPreview(text: string, origem: string, maxRows = 10): CsvPreview {
  const stripped = text.replace(/^﻿/, "");
  const firstLine = stripped.split(/\r?\n/, 1)[0] ?? "";
  const nComma = (firstLine.match(/,/g) ?? []).length;
  const nSemi = (firstLine.match(/;/g) ?? []).length;
  const nTab = (firstLine.match(/\t/g) ?? []).length;
  let normalized = stripped;
  if (nTab > nComma && nTab > nSemi) normalized = stripped.replace(/\t/g, ",");
  else if (nSemi > nComma) normalized = stripped.replace(/;/g, ",");

  const raw: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (normalized[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') inQuotes = true;
    else if (ch === ",") { row.push(field); field = ""; }
    else if (ch === "\n") { row.push(field); raw.push(row); row = []; field = ""; }
    else if (ch === "\r") { /* swallow */ }
    else field += ch;
  }
  if (field !== "" || row.length > 0) { row.push(field); raw.push(row); }

  if (raw.length === 0) return { origem, headers: [], rows: [], totalRows: 0, linhas: {} };
  const headers = (raw[0] ?? []).map((h) => h.trim());
  const dataLines = raw.slice(1).filter((r) => !(r.length === 1 && r[0] === ""));
  const toObj = (r: string[]) => {
    const obj: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) obj[headers[j]!] = (r[j] ?? "").trim();
    return obj;
  };
  const rows = dataLines.slice(0, maxRows).map(toObj);
  // Linha 1 = cabecalho, entao a linha de dado N (0-indexed) e' a linha N+2 do
  // arquivo — mesma convencao que o backend usa em todo erro reportado.
  const linhas: Record<number, Record<string, string>> = {};
  dataLines.forEach((r, i) => { linhas[i + 2] = toObj(r); });
  return { origem, headers, rows, totalRows: dataLines.length, linhas };
}

export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadCsv<T extends Record<string, unknown>>(filename: string, rows: T[]): void {
  if (rows.length === 0) {
    alert("Nada a exportar.");
    return;
  }
  const headers = Object.keys(rows[0]!);
  const escape = (v: unknown) => {
    if (v == null) return "";
    const s = typeof v === "string" ? v : JSON.stringify(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
